import type { ImageSourcePropType } from "react-native";
import type { DataPointSampleType, TestBody } from "@src/packages/content";
import testTubesIllustration from "../../../assets/images/you-icons/test-tubes.png";
import ultrasoundProbeIllustration from "../../../assets/images/ultrasound-probe.png";
import follicleCountIllustration from "../../../assets/images/follicle-count.png";
import follicleSizeIllustration from "../../../assets/images/follicle-size.png";
import endometrialLiningIllustration from "../../../assets/images/endometrial-lining.png";
import embryoGeneticsIllustration from "../../../assets/images/embryo-genetics.png";
import semenAnalysisIllustration from "../../../assets/images/semen-analysis.png";
import endometrialBiopsyIllustration from "../../../assets/images/endometrial-biopsy.png";
import endometrialBiopsyCollectionIllustration from "../../../assets/images/endometrial-biopsy-collection.png";
import amhOvarianReserveIllustration from "../../../assets/images/amh-ovarian-reserve.png";
import wholeGenomeSequencingIllustration from "../../../assets/images/whole-genome-sequencing.png";
import karyotypeIllustration from "../../../assets/images/karyotype.png";
import expandedCarrierScreeningIllustration from "../../../assets/images/expanded-carrier-screening.png";
import fertilityHormoneTrackerIllustration from "../../../assets/images/fertility-hormone-tracker.png";
import infectiousDiseasePanelIllustration from "../../../assets/images/infectious-disease-panel.png";
import thrombophiliaPanelIllustration from "../../../assets/images/thrombophilia-panel.png";
import endometrialMicrobiomeIllustration from "../../../assets/images/endometrial-microbiome.png";

const BASELINE_ULTRASOUND_TEST_ID = "test-baseline-ultrasound";
const AFC_TEST_ID = "test-afc";
const AMH_TEST_ID = "test-amh";
const WHOLE_GENOME_TEST_ID = "test-whole-genome";
const KARYOTYPE_TEST_ID = "test-karyotype";
const CARRIER_SCREENING_TEST_ID = "test-carrier-screening";
const FERTILITY_TRACKER_TEST_ID = "test-fertility-tracker";
const INFECTIOUS_DISEASE_TEST_ID = "test-infectious-disease";
const THROMBOPHILIA_TEST_ID = "test-thrombophilia";
const ENDOMETRIAL_MICROBIOME_TEST_ID = "test-endometrial-microbiome-testing";
const ENDOMETRIAL_BIOPSY_PROCEDURE_ID = "proc-endometrial-biopsy";
const PGT_TEST_IDS = new Set([
  "test-preimplantation-genetic-testing",
  "test-pgt-a",
  "test-pgt-m",
  "test-pgt-sr",
  "test-pgt-p",
]);
const FOLLICLE_COUNT_DATA_POINT_ID = "dp-follicle-count";
const FOLLICLE_SIZE_DATA_POINT_ID = "dp-follicle-size";
const ENDOMETRIAL_THICKNESS_DATA_POINT_ID = "dp-endometrial-thickness";

type TestIllustrationInput = {
  testId?: string;
  category?: TestBody["category"];
  sampleType?: DataPointSampleType;
  dataPointIds?: readonly string[];
  displayContext?: "test" | "marker";
};

function hasDataPoint(
  dataPointIds: readonly string[] | undefined,
  dataPointId: string,
): boolean {
  return dataPointIds?.some((id) => String(id) === dataPointId) ?? false;
}

export function getTestIllustration({
  testId,
  category,
  sampleType,
  dataPointIds,
  displayContext = "test",
}: TestIllustrationInput): ImageSourcePropType {
  const normalizedTestId = testId ? String(testId) : undefined;

  if (normalizedTestId === AFC_TEST_ID) {
    return ultrasoundProbeIllustration;
  }

  if (normalizedTestId === AMH_TEST_ID) {
    return amhOvarianReserveIllustration;
  }

  if (normalizedTestId === WHOLE_GENOME_TEST_ID) {
    return wholeGenomeSequencingIllustration;
  }

  if (normalizedTestId === KARYOTYPE_TEST_ID) {
    return karyotypeIllustration;
  }

  if (normalizedTestId === CARRIER_SCREENING_TEST_ID) {
    return expandedCarrierScreeningIllustration;
  }

  if (normalizedTestId === FERTILITY_TRACKER_TEST_ID) {
    return fertilityHormoneTrackerIllustration;
  }

  if (normalizedTestId === INFECTIOUS_DISEASE_TEST_ID) {
    return infectiousDiseasePanelIllustration;
  }

  if (normalizedTestId === THROMBOPHILIA_TEST_ID) {
    return thrombophiliaPanelIllustration;
  }

  if (normalizedTestId === ENDOMETRIAL_MICROBIOME_TEST_ID) {
    return endometrialMicrobiomeIllustration;
  }

  if (normalizedTestId === ENDOMETRIAL_BIOPSY_PROCEDURE_ID) {
    return endometrialBiopsyCollectionIllustration;
  }

  if (category === "genetic" || PGT_TEST_IDS.has(normalizedTestId ?? "")) {
    return embryoGeneticsIllustration;
  }

  if (category === "biopsy") {
    return endometrialBiopsyIllustration;
  }

  if (category === "semen" || sampleType === "semen") {
    return semenAnalysisIllustration;
  }

  if (sampleType === "ultrasound" || category === "imaging") {
    if (displayContext === "marker") {
      if (hasDataPoint(dataPointIds, FOLLICLE_COUNT_DATA_POINT_ID)) {
        return follicleCountIllustration;
      }

      if (hasDataPoint(dataPointIds, FOLLICLE_SIZE_DATA_POINT_ID)) {
        return follicleSizeIllustration;
      }

      if (hasDataPoint(dataPointIds, ENDOMETRIAL_THICKNESS_DATA_POINT_ID)) {
        return endometrialLiningIllustration;
      }
    }

    if (
      normalizedTestId === BASELINE_ULTRASOUND_TEST_ID ||
      category === "imaging" ||
      (dataPointIds?.length ?? 0) > 1
    ) {
      return ultrasoundProbeIllustration;
    }

    if (hasDataPoint(dataPointIds, FOLLICLE_COUNT_DATA_POINT_ID)) {
      return follicleCountIllustration;
    }

    if (hasDataPoint(dataPointIds, FOLLICLE_SIZE_DATA_POINT_ID)) {
      return follicleSizeIllustration;
    }

    if (hasDataPoint(dataPointIds, ENDOMETRIAL_THICKNESS_DATA_POINT_ID)) {
      return endometrialLiningIllustration;
    }

    return ultrasoundProbeIllustration;
  }

  return testTubesIllustration;
}
